import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "./store/actions/api/auth";
import { SessionSyncFrame } from "./SessionSyncFrame";
import DeveloperLanding from "./containers/DeveloperLanding";
import { redirectToLogin } from "./lib/accounts";
import { ROUTES, LANDING_PATHS } from "./routes";

// Tool pages render without sign-in; the tools gate their own "beyond" actions
// (see useAuthGate). Everything else stays behind auth.
// Legacy: these three opened their APP paths to anonymous visitors before
// PublicShell existed. New public surface gets a `/public` mount instead — do
// not extend this list (docs/CONVENTIONS.md §1.4).
const PUBLIC_PREFIXES = [ROUTES.thrustStand, ROUTES.logAnalyzer, ROUTES.propulsionCalc];
const isPublicPath = () => PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p));

// The app "home" paths show a public developer landing (explore + sign-in hook)
// for logged-out visitors instead of bouncing to the accounts login. Deep app
// routes still redirect.
const isHomePath = () => (window.location.pathname.replace(/\/+$/, "") || "/") === ROUTES.home;
const isLandingPath = () => {
  const p = window.location.pathname.replace(/\/+$/, "") || "/";
  return LANDING_PATHS.includes(p);
};


const isLocalTokenValid = () => {
  const token = localStorage.getItem("tk");
  const ext = localStorage.getItem("ext");
  if (!token || !ext) return false;
  const expiryInSeconds = Number(ext);
  return (
    Number.isFinite(expiryInSeconds) &&
    expiryInSeconds > Math.floor(Date.now() / 1000)
  );
};

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.tk !== null);
  const authChecked = useSelector((state) => state.auth.authChecked);
  const [sessionSyncTried, setSessionSyncTried] = useState(false);
  const [sessionRecheckKey, setSessionRecheckKey] = useState(0);
  const [isSessionRechecking, setIsSessionRechecking] = useState(false);

  const handleSessionFailed = useCallback(() => {
    if (isLocalTokenValid()) {
      setSessionSyncTried(true);
      return;
    }
    dispatch(actions.endLocalSession());
    setSessionSyncTried(true);
    if (!isPublicPath() && !isLandingPath()) redirectToLogin();
  }, [dispatch]);

  const handleSessionReceived = useCallback(({ token, email, exp }) => {
    const user = dispatch(actions.syncRemoteSession({ token, email, exp }));

    if (!user) {
      handleSessionFailed();
      return;
    }

    setSessionSyncTried(true);
    setIsSessionRechecking(false);
  }, [dispatch, handleSessionFailed]);

  const handleSessionRecheckFailed = useCallback(() => {
    if (isLocalTokenValid()) {
      setIsSessionRechecking(false);
      return;
    }
    dispatch(actions.endLocalSession());
    setIsSessionRechecking(false);
    setSessionSyncTried(true);
    redirectToLogin();
  }, [dispatch]);

  const handleSessionRecheckReceived = useCallback((session) => {
    handleSessionReceived(session);
    setIsSessionRechecking(false);
  }, [handleSessionReceived]);

  useEffect(() => {
    if (!authChecked && !sessionSyncTried) {
      const timeout = setTimeout(() => {
        handleSessionFailed();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [authChecked, sessionSyncTried, handleSessionFailed]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const refreshRemoteSession = () => {
      setSessionRecheckKey((key) => key + 1);
      setIsSessionRechecking(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshRemoteSession();
      }
    };

    window.addEventListener("focus", refreshRemoteSession);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(refreshRemoteSession, 60000);

    return () => {
      window.removeEventListener("focus", refreshRemoteSession);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isSessionRechecking) return undefined;

    const timeout = setTimeout(() => {
      setIsSessionRechecking(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isSessionRechecking, sessionRecheckKey]);

  if (!authChecked && !sessionSyncTried) {
    return (
      <SessionSyncFrame
        onSessionReceived={handleSessionReceived}
        onSessionFailed={handleSessionFailed}
      />
    );
  }

  // "/" is the public developer homepage for everyone — signed-in visitors get
  // an "Access the app" link on it rather than landing in the dashboard.
  if (isHomePath()) return <DeveloperLanding />;

  if (!isAuthenticated) {
    if (isLandingPath()) return <DeveloperLanding />;
    if (isPublicPath()) return <>{children}</>;
    redirectToLogin();
    return null;
  }

  return (
    <>
      {children}
      {isSessionRechecking && (
        <SessionSyncFrame
          key={sessionRecheckKey}
          onSessionReceived={handleSessionRecheckReceived}
          onSessionFailed={handleSessionRecheckFailed}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
